import { useMemo } from 'react'
import { z } from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import type { SignUpPayload } from '@/shared/hooks/api/auth'
import { Loader2, UserPlus } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/shared/lib/utils'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { PasswordInput } from '@/components/password-input'

export type SignUpFormValues = {
  firstName: string
  lastName: string
  email: string
  password: string
  confirmPassword: string
}

type SignUpFormProps = React.HTMLAttributes<HTMLFormElement> & {
  onSignUp: (payload: SignUpPayload) => Promise<void>
  isSigningUp: boolean
}

export function SignUpForm({
  className,
  onSignUp,
  isSigningUp,
  ...props
}: SignUpFormProps) {
  const { t } = useTranslation('auth')

  const signUpFormSchema = useMemo(
    () =>
      z
        .object({
          firstName: z.string().min(1, t('signUp.validation.firstNameRequired')),
          lastName: z.string().min(1, t('signUp.validation.lastNameRequired')),
          email: z.string().email(t('signUp.validation.emailInvalid')),
          password: z.string().min(8, t('signUp.validation.passwordMin')),
          confirmPassword: z
            .string()
            .min(1, t('signUp.validation.confirmPasswordRequired')),
        })
        .refine((data) => data.password === data.confirmPassword, {
          message: t('signUp.validation.passwordsMismatch'),
          path: ['confirmPassword'],
        }),
    [t]
  )

  const form = useForm<SignUpFormValues>({
    resolver: zodResolver(signUpFormSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      confirmPassword: '',
    },
  })

  async function onSubmit(data: SignUpFormValues) {
    await onSignUp({
      firstName: data.firstName.trim(),
      lastName: data.lastName.trim(),
      email: data.email.trim(),
      password: data.password,
    })
  }

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className={cn('grid gap-3', className)}
        {...props}
      >
        <div className="grid items-start gap-3 sm:grid-cols-2">
          <FormField
            control={form.control}
            name="firstName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('signUp.fields.firstName.label')}</FormLabel>
                <FormControl>
                  <Input autoComplete="given-name" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="lastName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('signUp.fields.lastName.label')}</FormLabel>
                <FormControl>
                  <Input autoComplete="family-name" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('signUp.fields.email.label')}</FormLabel>
              <FormControl>
                <Input
                  type="email"
                  autoComplete="email"
                  placeholder={t('signUp.fields.email.placeholder')}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="password"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('signUp.fields.password.label')}</FormLabel>
              <FormControl>
                <PasswordInput
                  autoComplete="new-password"
                  placeholder="********"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="confirmPassword"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('signUp.fields.confirmPassword.label')}</FormLabel>
              <FormControl>
                <PasswordInput
                  autoComplete="new-password"
                  placeholder="********"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <Button className="mt-2" type="submit" disabled={isSigningUp}>
          {isSigningUp ? <Loader2 className="animate-spin" /> : <UserPlus />}
          {t('signUp.actions.submit')}
        </Button>
      </form>
    </Form>
  )
}
